// src/pages/MarkAttendance.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import moment from "moment";
import api from "../api";
import Swal from "sweetalert2";
import "./Attendance.css";

const statuses = ["present", "absent", "late", "leave", "halfday", "preparatory_off", "holiday"];

// --- URL helpers ---
const getApiBase = () =>
  (process.env.REACT_APP_API_URL || api?.defaults?.baseURL || "").replace(/\/+$/, "");

const getUploadBase = () => getApiBase().replace(/\/api(?:\/v\d+)?$/, "");

const buildPhotoURL = (photo) => {
  if (!photo) return null;
  if (/^https?:\/\//i.test(photo)) return photo;
  const base = getUploadBase();
  return `${base}/uploads/photoes/students/${photo}`;
};

const MarkAttendance = () => {
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [remarks, setRemarks] = useState({});
  const [savedRemarks, setSavedRemarks] = useState({});
  const [holidayReason, setHolidayReason] = useState("");
  const [savedAttendance, setSavedAttendance] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [mode, setMode] = useState("create"); // "create" or "edit"
  const [loading, setLoading] = useState(false);
  const [poTreatment, setPoTreatment] = useState("excluded");

  const [teacherClassId, setTeacherClassId] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [query, setQuery] = useState("");

  // Fetch students for the incharge
  const fetchStudents = async () => {
    try {
      const { data } = await api.get("/incharges/students");
      const fetchedStudents = Array.isArray(data?.students) ? data.students : [];

      fetchedStudents.sort((a, b) => {
        const byName = (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
        if (byName !== 0) return byName;
        return (a.admission_number || "").localeCompare(b.admission_number || "", undefined, {
          numeric: true,
        });
      });

      setStudents(fetchedStudents);

      if (fetchedStudents.length > 0) {
        setTeacherClassId(fetchedStudents[0].class_id || null);
      }

      setAttendance({});
    } catch (error) {
      console.error("Error fetching students for attendance:", error);
      Swal.fire("Error", "Failed to fetch students.", "error");
    }
  };

  // Fetch holidays from the API
  const fetchHolidays = async () => {
    try {
      const { data } = await api.get("/holidays");
      setHolidays(data);
    } catch (error) {
      console.error("Error fetching holidays:", error);
    }
  };

  const fetchAttendanceSettings = async () => {
    try {
      const { data } = await api.get("/attendance/settings");
      setPoTreatment(data?.preparatory_off_treatment || "excluded");
    } catch (error) {
      console.error("Error fetching attendance settings:", error);
    }
  };

  // Fetch attendance records for a given date (memoized for hook deps)
  const fetchAttendanceForDate = useCallback(
    async (date) => {
      try {
        const { data } = await api.get(`/attendance/date/${date}`);
        const inchargeStudentIds = new Set(students.map((student) => String(student.id)));
        const scopedRecords = Array.isArray(data)
          ? data.filter((record) =>
              inchargeStudentIds.has(String(record.studentId ?? record.student_id))
            )
          : [];

        const loadedRemarks = Object.fromEntries(scopedRecords.map((record) => [record.studentId ?? record.student_id, record.remarks || ""]));
        setRemarks(loadedRemarks);
        setSavedRemarks(loadedRemarks);
        setHolidayReason(scopedRecords.find((record) => record.status === "holiday")?.remarks || "");
        if (scopedRecords.length > 0) {
          setMode("edit");
          const attendanceMap = {};
          scopedRecords.forEach((record) => {
            const studentId = record.studentId ?? record.student_id;
            attendanceMap[studentId] = record.status;
          });
          setAttendance(attendanceMap);
          setSavedAttendance(attendanceMap);
        } else {
          setMode("create");
          setAttendance({});
          setSavedAttendance({});
        }
      } catch (error) {
        console.error("Error fetching attendance for date:", error);
        setRemarks({});
        setSavedRemarks({});
        setHolidayReason("");
        setMode("create");
        setAttendance({});
        setSavedAttendance({});
      }
    },
    [students]
  );

  useEffect(() => {
    fetchStudents();
    fetchHolidays();
    fetchAttendanceSettings();
  }, []);

  useEffect(() => {
    if (students.length) {
      fetchAttendanceForDate(selectedDate);
    }
  }, [selectedDate, students, fetchAttendanceForDate]);

  const handleAttendanceChange = (studentId, status) => {
    setAttendance((prev) => ({ ...prev, [studentId]: status }));
  };

  const handleMarkAll = (status) => {
    const updatedAttendance = {};
    students.forEach((student) => {
      updatedAttendance[student.id] = status;
    });
    setAttendance(updatedAttendance);
    if (status === "holiday") setRemarks((previous) => ({ ...previous, ...Object.fromEntries(students.map((student) => [student.id, holidayReason.trim()])) }));
  };

  const summaryCounts = useMemo(() => {
    const acc = { present: 0, absent: 0, late: 0, leave: 0, halfday: 0, preparatory_off: 0, holiday: 0 };
    students.forEach((student) => {
      const status = attendance[student.id];
      if (status && Object.prototype.hasOwnProperty.call(acc, status)) {
        acc[status] += 1;
      }
    });
    return acc;
  }, [students, attendance]);

  const totalStudents = students.length;

  const hasUnsavedChanges = useMemo(() => {
    return students.some(
      (student) =>
        (attendance[student.id] || "") !==
        (savedAttendance[student.id] || "") ||
        (remarks[student.id] || "").trim() !== (savedRemarks[student.id] || "").trim()
    );
  }, [attendance, savedAttendance, remarks, savedRemarks, students]);

  const filteredStudents = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return students;
    return students.filter(
      (s) =>
        (s.name || "").toLowerCase().includes(q) ||
        (s.admission_number || "").toLowerCase().includes(q)
    );
  }, [students, query]);

  const getRowClass = (status) => {
    switch (status) {
      case "absent":
        return "table-danger";
      case "late":
        return "table-warning";
      case "leave":
        return "table-info";
      case "halfday":
        return "table-primary";
      case "holiday":
      case "preparatory_off":
        return "table-secondary";
      default:
        return "";
    }
  };

  const statusLabel = (status) =>
    status === "preparatory_off" ? "PO" : status === "halfday" ? "Half Day" : status;

  const poTreatmentLabel =
    poTreatment === "present"
      ? "PO counts as Present"
      : poTreatment === "holiday"
      ? "PO is treated as Holiday / non-working day"
      : "PO is excluded from Present, Absent and working-day percentage";

  const classLabel = useMemo(() => {
    const first = students[0];
    const cn = first?.class_name || first?.Class?.class_name;
    const sn = first?.section_name || first?.Section?.section_name;
    if (!cn && !sn) return null;
    return `${cn || "Class"}${sn ? ` - ${sn}` : ""}`;
  }, [students]);

  const selectedMoment = moment(selectedDate, "YYYY-MM-DD");
  const today = moment().startOf("day");

  // ---- Submit the complete class atomically in one request ----
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const records = students.filter((student) => attendance[student.id]).map((s) => ({
        studentId: s.id,
        status: attendance[s.id],
        remarks: remarks[s.id]?.trim() || null,
      }));

      if (!records.length) return;

      const { data } = await api.post("/attendance/bulk", {
        date: selectedDate,
        records,
      });
      Swal.fire(
        mode === "create" ? "Success" : "Saved",
        `${data?.saved ?? records.length} student attendance records saved.`,
        "success"
      );

      await fetchAttendanceForDate(selectedDate);
    } catch (err) {
      console.error("Save attendance failed:", err);
      Swal.fire("Error", err?.response?.data?.message || "Failed to save attendance.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUnmarkAttendance = async () => {
    if (mode !== "edit" || !students.length) return;

    const result = await Swal.fire({
      title: "Unmark attendance?",
      html: `This will remove the marked attendance for <strong>${moment(selectedDate).format("LL")}</strong> for this class.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Unmark",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    setLoading(true);
    try {
      const { data } = await api.post("/attendance/unmark", {
        date: selectedDate,
        studentIds: students.map((student) => student.id),
      });

      await Swal.fire(
        "Unmarked",
        `${data?.deleted ?? 0} attendance record${Number(data?.deleted ?? 0) === 1 ? "" : "s"} removed. You can mark attendance again now.`,
        "success"
      );

      await fetchAttendanceForDate(selectedDate);
    } catch (err) {
      console.error("Unmark attendance failed:", err);
      Swal.fire(
        "Error",
        err?.response?.data?.message || "Failed to unmark attendance.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUnmarkStudent = async (student) => {
    if (loading || !savedAttendance[student.id]) return;
    setLoading(true);
    try {
      const result = await Swal.fire({
        title: "Unmark student attendance?",
        text: `Remove attendance for ${student.name} on ${moment(selectedDate).format("LL")}? Other students will remain unchanged.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Unmark",
        confirmButtonColor: "#dc3545",
      });
      if (!result.isConfirmed) return;
      await api.post("/attendance/unmark", { date: selectedDate, studentIds: [student.id] });
      // Update only this row, preserving unsaved edits for other students.
      const withoutStudent = (previous) => {
        const next = { ...previous };
        delete next[student.id];
        return next;
      };
      setAttendance(withoutStudent);
      setSavedAttendance(withoutStudent);
      setRemarks(withoutStudent);
      setSavedRemarks(withoutStudent);
      await Swal.fire("Unmarked", `Attendance for ${student.name} has been unmarked.`, "success");
    } catch (error) {
      Swal.fire("Error", error?.response?.data?.message || "Failed to unmark student attendance.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ---- Early returns for invalid dates/holidays ----
  if (selectedMoment.isAfter(today)) {
    return (
      <div className="container mt-4">
        <header className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <h1 className="h3 m-0">Mark Attendance</h1>
          <div>
            <input
              type="date"
              disabled={loading}
              id="attendanceDate"
              className="form-control"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
        </header>
        <div className="alert alert-info">Attendance cannot be marked for future dates.</div>
      </div>
    );
  }

  if (selectedMoment.day() === 0) {
    return (
      <div className="container mt-4">
        <header className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <h1 className="h3 m-0">Mark Attendance</h1>
          <div>
            <input
              type="date"
              disabled={loading}
              id="attendanceDate"
              className="form-control"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
        </header>
        <div className="alert alert-info">{selectedMoment.format("LL")} is Sunday. No attendance required.</div>
      </div>
    );
  }

  const holidayForDate = holidays.find(
    (holiday) =>
      holiday.date === selectedDate &&
      teacherClassId &&
      holiday.class &&
      Number(holiday.class.id) === Number(teacherClassId)
  );
  if (holidayForDate) {
    return (
      <div className="container mt-4">
        <header className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <h1 className="h3 m-0">Mark Attendance</h1>
          <div>
            <input
              type="date"
              disabled={loading}
              id="attendanceDate"
              className="form-control"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
        </header>
        <div className="alert alert-info">
          {selectedMoment.format("LL")} is a holiday: <strong>{holidayForDate.description}</strong>
        </div>
      </div>
    );
  }

  // ---- UI ----
  return (
    <div className="container mt-4">
      {/* Header Bar */}
      <header className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <div>
          <h1 className="h3 m-0">Mark Attendance</h1>
          {classLabel && <div className="text-muted small">{classLabel} • {moment(selectedDate).format("LL")}</div>}
        </div>
        <div className="d-flex gap-2">
          <input
            type="date"
              disabled={loading}
            id="attendanceDate"
            className="form-control"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
          <input
            type="text"
            className="form-control"
            placeholder="Search by name or admission no..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </header>

      {/* Summary Cards */}
      <div className="row g-3 mb-2">
        <div className="col-6 col-md-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="text-muted small">Total Students</div>
              <div className="fs-4 fw-semibold">{totalStudents}</div>
            </div>
          </div>
        </div>
        {statuses.map((status) => (
          <div className="col-6 col-md-3" key={status}>
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="text-muted small text-capitalize">{statusLabel(status)}</div>
                  <span
                    className={`badge rounded-pill ${
                      status === "absent" ? "bg-danger" :
                      status === "late" ? "bg-warning" :
                      status === "leave" ? "bg-info" :
                      status === "halfday" ? "bg-primary" :
                      status === "preparatory_off" ? "bg-secondary" :
                      "bg-success"
                    }`}
                  >
                    {summaryCounts[status]}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="alert alert-light border d-flex align-items-center justify-content-between flex-wrap gap-2 py-2 mb-3">
        <div>
          <strong>PO = Preparatory Off</strong>
          <span className="text-muted ms-2">{poTreatmentLabel}</span>
        </div>
        <span className="badge text-bg-secondary">Controlled by Admin / Coordinator</span>
      </div>

      <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
        <div>
          <label className="form-label small" htmlFor="class-holiday-reason">Holiday reason (optional)</label>
          <input id="class-holiday-reason" className="form-control form-control-sm" maxLength={255}
            placeholder="e.g. Diwali" value={holidayReason} disabled={loading}
            onChange={(event) => {
              const value = event.target.value;
              setHolidayReason(value);
              setRemarks((previous) => ({ ...previous, ...Object.fromEntries(students.filter((student) => attendance[student.id] === "holiday").map((student) => [student.id, value])) }));
            }} />
        </div>
        <button className="btn btn-outline-secondary btn-sm" disabled={loading || !students.length} onClick={() => handleMarkAll("holiday")}>All Holiday</button>
        <small className="text-muted">Applies to the whole class. Save attendance to confirm.</small>
      </div>

      {/* Compact Actions near cards */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <div className="btn-group btn-group-sm" role="group" aria-label="Quick actions">
          <button className="btn btn-outline-success" disabled={loading} onClick={() => handleMarkAll("present")}>
            All Present
          </button>
          <button className="btn btn-outline-danger" disabled={loading} onClick={() => handleMarkAll("absent")}>
            All Absent
          </button>
          <button className="btn btn-outline-primary" disabled={loading} onClick={() => handleMarkAll("halfday")}>
            All Halfday
          </button>
          <button className="btn btn-outline-secondary" disabled={loading} onClick={() => handleMarkAll("preparatory_off")}>
            All PO
          </button>
        </div>

        <div className="d-flex align-items-center gap-2">
          {mode === "edit" && (
            <span className={`badge ${hasUnsavedChanges ? "text-bg-warning" : "text-bg-success"}`}>
              {hasUnsavedChanges ? "Changes not saved" : "Attendance already marked ✓"}
            </span>
          )}
          {mode === "edit" && (
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={handleUnmarkAttendance}
              disabled={loading}
              title="Remove the marked attendance for this class and date"
            >
              Unmark Attendance
            </button>
          )}
          <button
            className="btn btn-primary btn-sm"
            onClick={handleSubmit}
            disabled={loading || !hasUnsavedChanges}
          >
            {loading
              ? "Working..."
              : mode === "create"
              ? "Submit"
              : hasUnsavedChanges
              ? "Update"
              : "Marked"}
          </button>
        </div>
      </div>

      {/* Attendance Table */}
      <div className="table-responsive">
        <table className="table align-middle table-striped">
          <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
            <tr>
              <th style={{ width: 56 }}>#</th>
              <th>Admission No.</th>
              <th>Name</th>
              <th>Attendance</th>
            </tr>
          </thead>
          <tbody>
            {filteredStudents.length ? (
              filteredStudents.map((s, index) => (
                <tr key={s.id} className={getRowClass(attendance[s.id])}>
                  <td>{index + 1}</td>
                  <td>
                    <span className="badge bg-secondary-subtle text-dark border" title="Admission Number">
                      {s.admission_number || "—"}
                    </span>
                  </td>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      {(() => {
                        const photoUrl = buildPhotoURL(s.photo);
                        return photoUrl ? (
                          <img
                            src={photoUrl}
                            alt={s.name}
                            width={32}
                            height={32}
                            className="rounded-circle object-fit-cover border"
                          />
                        ) : (
                          <div
                            className="rounded-circle bg-light border d-inline-flex align-items-center justify-content-center"
                            style={{ width: 32, height: 32 }}
                          >
                            <span className="small text-muted">N/A</span>
                          </div>
                        );
                      })()}
                      <div>
                        <div className="fw-semibold">{s.name}</div>
                        <div className="text-muted small">Roll: {s.roll_number ?? "—"}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="d-flex flex-wrap gap-3">
                      {statuses.map((status) => (
                        <label key={status} className="form-check form-check-inline m-0">
                          <input
                            type="radio"
                            disabled={loading}
                            name={`attendance-${s.id}`}
                            value={status}
                            checked={attendance[s.id] === status}
                            onChange={(e) => handleAttendanceChange(s.id, e.target.value)}
                            className="form-check-input"
                          />
                          <span className="form-check-label text-capitalize">{statusLabel(status)}</span>
                        </label>
                      ))}
                    </div>
                    <div className="d-flex align-items-center gap-2 mt-2">
                      {!attendance[s.id] && <span className="badge text-bg-light border">Unmarked</span>}
                      {savedAttendance[s.id] && <button type="button"
                        className="btn btn-outline-danger btn-sm" disabled={loading}
                        aria-label={`Unmark attendance for ${s.name}`}
                        onClick={() => handleUnmarkStudent(s)}>Unmark</button>}
                    </div>
                    {attendance[s.id] === "holiday" && <input
                      disabled={loading}
                      className="form-control form-control-sm mt-2" maxLength={255}
                      aria-label={`Holiday reason for ${s.name}`} placeholder="Holiday reason (optional)"
                      value={remarks[s.id] || ""}
                      onChange={(event) => setRemarks((previous) => ({ ...previous, [s.id]: event.target.value }))}
                    />}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="text-center text-muted">No students found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Fixed Action Bar */}
      <div
        className="d-sm-none fixed-bottom bg-white border-top shadow-sm px-3 py-2"
        style={{
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 8px)",
        }}
      >
        <div className="d-flex align-items-center justify-content-between gap-2">
          <div className="btn-group btn-group-sm" role="group" aria-label="Quick actions mobile">
            <button className="btn btn-outline-success" disabled={loading} onClick={() => handleMarkAll("present")}>
              All Present
            </button>
            <button className="btn btn-outline-danger" disabled={loading} onClick={() => handleMarkAll("absent")}>
              All Absent
            </button>
            <button className="btn btn-outline-primary" disabled={loading} onClick={() => handleMarkAll("halfday")}>
              All Halfday
            </button>
          </div>
          <div className="d-flex align-items-center gap-1">
            {mode === "edit" && (
              <button
                className="btn btn-outline-danger btn-sm"
                onClick={handleUnmarkAttendance}
                disabled={loading}
                title="Unmark attendance"
              >
                Unmark
              </button>
            )}
            <button
              className="btn btn-primary btn-sm"
              onClick={handleSubmit}
              disabled={loading || !hasUnsavedChanges}
            >
              {loading
                ? "Working..."
                : mode === "create"
                ? "Submit"
                : hasUnsavedChanges
                ? "Update"
                : "Marked ✓"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarkAttendance;
